<template>
    <div class="flex mb-px draggable relative" :style="'background-color:#FFF'" :key="itemKey">
        <!-- Draggable handle. -->
        <div class="w-6 flex-shrink-0 py-1.5 -my-px border-t">
            <div class="w-5 h-5 flex items-center">
                <i class="fa-fw fa fa-bars text-xs text-gray-300 hover:text-gray-400 cursor-move pl-1 handle"
                   v-if="!estimate.readonly"></i>
            </div>
        </div>

        <!-- Item -->
        <div class="flex-1">
            <!-- Main Item -->
            <div class="flex">
                <!-- Description. -->
                <div class="flex-1 flex m-0 relative">
                    <div
                        :key="renderKey"
                        class="flex flex-1 flex-col m-0 -ml-px -mt-px mr-0 -mb-px border border-gray-200 shadow-none"
                        :style="'background-color: '+item.color"
                        :class="estimate.readonly?'':'hover:border-gray-400 hover:z-10'">
                        <template v-if="estimate.readonly">
                            <div
                                class="text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative whitespace-pre-wrap focus:border-blue-500 border-none focus:z-20 resize-none"
                                :style="'background-color: '+item.color+';border-color: '+item.color"
                                v-text="item.description">
                            </div>
                        </template>
                        <autoresizable-textarea :height="el => (el.scrollHeight + 2) + 'px'"
                                                ref="autoresizable-description" v-else
                        >
                        <textarea
                            placeholder="Description..."
                            class="text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            type="text"
                            ref="description"
                            :disabled="estimate.readonly"
                            v-model="item.description"
                            @input="updateEstimate()"
                            @focus=""
                            @change="changeEstimate()"
                            rows="1"
                        ></textarea>
                        </autoresizable-textarea>
                    </div>
                </div>

                <!-- Unit -->
                <div class="w-20 m-0 flex">
                    <div
                        class="flex flex-1 flex-col -ml-px -mt-px mr-0 -mb-px border border-gray-200 shadow-none hover:z-10"
                        :style="'background-color: '+item.color"
                        :class="estimate.readonly?'':'hover:border-gray-400'">
                        <page-cell
                            class="flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none min-h-[34px]"
                            :style="'background-color: '+item.color"
                            placeholder="Unit"
                            :read-only="estimate.readonly"
                            v-model="item.unit"
                            @input="estimate.update()"
                            @change="estimate.addSnapshot()"
                        ></page-cell>
                    </div>
                </div>

                <!-- Rate. -->
                <div class="w-28 m-0 flex">
                    <div
                        class="flex flex-1 flex-col -ml-px -mt-px mr-0 -mb-px border border-gray-200 shadow-none hover:z-10"
                        :style="'background-color: '+item.color"
                        :class="estimate.readonly?'':'hover:border-gray-400'">
                        <page-cell
                            currency
                            class="text-right flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none min-h-[34px]"
                            :placeholder="item.quantity?(item.total/item.quantity):0 | toCurrency()"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            :read-only="estimate.readonly"
                            v-model="rate"
                            @input="updateRate"
                            @blur="updateAll()"
                            @change="updateAll();estimate.addSnapshot()"
                        ></page-cell>
                    </div>
                </div>
                <!-- Dropdown. -->
                <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t pl-1 pr-1">
                    <ui-dropdown menu-class="p-3 w-56 z-50" :enabled="inView" v-if="!estimate.readonly"
                                 :close-on-mouse-out-scroll="true">
                        <template #default="{ toggle }">
                            <button @click="toggle"
                                    class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                                <i class="fa fa-ellipsis-h"></i>
                            </button>
                        </template>
                        <template #menu="{ close }">
                            <ui-dropdown-item v-if="!estimate.readonly" icon="fas fa-trash" danger
                                              @mousedown="deleteItem();close();">Delete
                            </ui-dropdown-item>
                        </template>
                    </ui-dropdown>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import draggable from 'vuedraggable'
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import {cloneDeep, isNull} from "lodash";
import {resolveNumericText, toCurrency} from "../../../utils";
import AutoresizableTextarea from "../../ui/AutoresizableTextarea";
import UiDropdown from "../../ui/UiDropdown.vue";
import PageCell from "../../ui/PageCell.vue";
import * as math from 'mathjs';
import remoteStorage from "../../../utils/remoteStorage";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import {Estimate} from "@models";

export default {
    components: {
        UiDropdownDivider,
        PageCell, UiDropdown, draggable, AutoresizableTextarea, UiDropdownItem
    },
    props: {
        estimate: Object,
        item: Object,
        store: Object,
    },
    data() {
        return {
            rate: this.item.rate,
            editing: false,
            resolveNumericText,
            itemKey: 0,
            renderKey: 0,
            newItem: {
                description: null,
            },
            distance: {top: 0, bottom: 0},
            inView: this.item.isNew,
        }
    },
    watch: {
        item: {
            handler(newValue, oldValue) {
                if (this.editing !== 'rate') {
                    this.rate = math.round((this.item.quantity ? (this.item.total / this.item.quantity) : 0), 2)
                }
            },
            deep: true,
        },
    },
    methods: {
        isNull,
        round(a, b) {
            if (a) {
                return math.round(a, 0 + b);
            }
            return 0;
        },
        isNumeric(num) {
            return (typeof (num) === 'number' || typeof (num) === "string" && num.trim() !== '') && !isNaN(num);
        },
        deleteItem() {
            this.estimate.dayworkRates = this.estimate.dayworkRates.filter((item) => item !== this.item)
            this.estimate.update()
            this.estimate.addSnapshot()
            this.$emit('reflow')
        },
        updateRate(val) {
            this.editing = 'rate'
            this.item.rate = math.round(this.resolveNumericText(val), 3);
            this.estimate.update()
        },
        updateAll(val) {
            this.editing = false
            this.rate = math.round(this.item.rate, 3)
        },
        setIsInView(val) {
            if (this.inView !== val) {
                this.inView = val
                return true
            }
            return false
        },
        changeEstimate() {
            let loc = document.getElementById('ui-crud-main-body').scrollTop;
            this.estimate.addSnapshot()
            document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            this.$nextTick(() => {
                document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            })
        },
        updateEstimate() {
            let loc = document.getElementById('ui-crud-main-body').scrollTop;
            this.estimate.update()
            document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            this.$nextTick(() => {
                document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            })
        },
    },
    mounted() {
    },
}
</script>
